// ---------------------------------------------------------------------------
// BK-E: Per-tenant booking reminder templates
// ---------------------------------------------------------------------------
// Reminder content (subject + body) was previously hardcoded inside
// `packages/service-delivery/.../booking-reminder-service.ts`. That made every
// tenant share the same copy, blocked localisation, and prevented agencies
// from matching brand voice. This table lets a tenant override the rendered
// copy per channel + lead-time slot. The reminder dispatcher reads the row
// keyed by `(tenant_id, channel, lead_time_seconds)` and falls back to the
// hardcoded default when no row exists, so adoption is incremental.
// ---------------------------------------------------------------------------

import {
  pgTable,
  uuid,
  text,
  integer,
  boolean,
  timestamp,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

export const REMINDER_TEMPLATE_CHANNELS = ["sms", "email", "whatsapp"] as const;
export type ReminderTemplateChannel = (typeof REMINDER_TEMPLATE_CHANNELS)[number];

export const bookingReminderTemplates = pgTable(
  "booking_reminder_templates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    // channel — restricted to ReminderTemplateChannel at the application layer
    channel: text("channel").notNull(),
    // lead_time_seconds — how long BEFORE the booking the reminder should fire
    // (e.g. 86400 for 24h, 7200 for 2h). Seconds (not minutes) so future
    // sub-minute reminders can be modelled without another migration.
    leadTimeSeconds: integer("lead_time_seconds").notNull(),
    // subject — only used by email/whatsapp; SMS ignores it
    subject: text("subject"),
    // body — required; supports {{handlebars}} variable substitution rendered
    // by buildBookingReminderPayload at dispatch time
    body: text("body").notNull(),
    enabled: boolean("enabled").notNull().default(true),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    // One template per (tenant, channel, lead_time). The dispatcher does an
    // exact match — overlapping rows would create non-deterministic copy.
    uniqueIndex("ux_booking_reminder_templates_slot").on(
      table.tenantId,
      table.channel,
      table.leadTimeSeconds,
    ),
    // Lookup path for the dispatcher: scan tenant's enabled templates and
    // pick the slot matching the upcoming reminder.
    index("idx_booking_reminder_templates_tenant").on(
      table.tenantId,
      table.enabled,
    ),
  ],
);

export type BookingReminderTemplate = typeof bookingReminderTemplates.$inferSelect;
export type NewBookingReminderTemplate = typeof bookingReminderTemplates.$inferInsert;
